/*
 * CDR report: the shared per-call report (call-report/CallReportView) across
 * one or more DIDs. One row per call, counted like the Incoming / Outgoing
 * Summary. Gated on 'CDR Report' read once the user's ACL is known.
 */
import { useMemo } from 'react';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAclVersion } from '@/hooks/useSessionRefresh';
import { CDR_REPORT, CDR_TRACE_REPORT } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import CallReportView, { CALL_SEARCH_BY_OPTIONS } from './call-report/CallReportView';

export const CDR_SEARCH_BY_OPTIONS = CALL_SEARCH_BY_OPTIONS;
export { formatDateTime } from './call-report/callReportColumns';

const buildUrl = (query: string) => `${CDR_REPORT}?${query}`;

export default function CdrReport() {
  const aclVersion = useAclVersion();
  const ops = useMemo(() => parseModuleOperations(getUser()?.acl_settings?.module_operations) || {}, [aclVersion]);

  if (Object.keys(ops).length > 0 && !hasPermission(ops, 'CDR Report', 'read')) {
    return <div className="my-12 p-4 text-center text-base">No read permission found in assigned ACL</div>;
  }
  const role = getUser()?.role;
  const canDetailedReport =
    role !== USER_ROLES.CALL_CENTRE_AGENT &&
    role !== USER_ROLES.CALL_CENTRE_TEAM_LEAD &&
    (Object.keys(ops).length === 0 || hasPermission(ops, 'Download Report', 'add'));

  return (
    <FillPage>
      <PageHeader
        title="Call Detail Report"
        description="One row per call across one or more DIDs, counted like the Incoming / Outgoing Summary, for the selected date range."
      />
      <CallReportView
        persistKey="ivr-reports/CdrReport"
        buildUrl={buildUrl}
        traceUrlBase={CDR_TRACE_REPORT}
        detailedReport={canDetailedReport}
      />
    </FillPage>
  );
}
